import React, { useState } from 'react';
import {
  Box, TextField, InputAdornment, Button, Collapse,
  Grid, MenuItem, FormControl, InputLabel, Select, Checkbox, ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const SELECT_ALL = '__select_all__';

function summarizeSelection(selectedVals, options) {
  if (options?.length > 0 && selectedVals.length === options.length) return 'All selected';
  const labels = selectedVals.map((val) => {
    const opt = options?.find((o) => (o.value ?? o) === val);
    return opt?.label ?? opt ?? val;
  });
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
}

export default function SearchFilterBar({ onSearch, onFilter, filters = [], searchPlaceholder = 'Search...' }) {
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState({});

  const handleSearch = (e) => {
    e.preventDefault();
    onSearch?.(search);
  };

  const handleFilterChange = (key, value) => {
    const updated = { ...filterValues, [key]: value };
    setFilterValues(updated);
    onFilter?.(updated);
  };

  const handleMultiSelectChange = (f, value) => {
    if (value.includes(SELECT_ALL)) {
      const allValues = (f.options || []).map((o) => o.value ?? o);
      const alreadyAllSelected = (filterValues[f.key] || []).length === allValues.length;
      handleFilterChange(f.key, alreadyAllSelected ? [] : allValues);
      return;
    }
    handleFilterChange(f.key, value);
  };

  const handleClear = () => {
    setSearch('');
    setFilterValues({});
    onSearch?.('');
    onFilter?.({});
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          sx={{ flex: 1, maxWidth: 400 }}
        />
        <Button type="submit" variant="contained" size="small">Search</Button>
        {filters.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
        )}
        <Button variant="text" size="small" startIcon={<ClearIcon />} onClick={handleClear}>
          Clear
        </Button>
      </Box>

      {filters.length > 0 && (
        <Collapse in={showFilters}>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {filters.map((f) => (
              <Grid item xs={12} sm={6} md={3} key={f.key}>
                {f.type === 'select' ? (
                  <FormControl size="small" fullWidth>
                    <InputLabel>{f.label}</InputLabel>
                    <Select
                      label={f.label}
                      multiple={!!f.multiple}
                      value={f.multiple ? (filterValues[f.key] || []) : (filterValues[f.key] || '')}
                      onChange={(e) => f.multiple ? handleMultiSelectChange(f, e.target.value) : handleFilterChange(f.key, e.target.value)}
                      MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                      renderValue={f.multiple ? (selectedVals) => summarizeSelection(selectedVals, f.options) : undefined}
                      sx={f.multiple ? {
                        '& .MuiSelect-select': {
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        },
                      } : undefined}
                    >
                      {!f.multiple && <MenuItem value="">All</MenuItem>}
                      {f.multiple && f.options?.length > 0 && (
                        <MenuItem value={SELECT_ALL} dense>
                          <Checkbox
                            size="small"
                            checked={(filterValues[f.key] || []).length === f.options.length}
                            indeterminate={
                              (filterValues[f.key] || []).length > 0 &&
                              (filterValues[f.key] || []).length < f.options.length
                            }
                            sx={{ p: 0.5, mr: 0.5 }}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                      )}
                      {f.options?.map((opt) => {
                        const val = opt.value ?? opt;
                        const label = opt.label ?? opt;
                        if (f.multiple) {
                          const checked = (filterValues[f.key] || []).includes(val);
                          return (
                            <MenuItem key={val} value={val} dense>
                              <Checkbox size="small" checked={checked} sx={{ p: 0.5, mr: 0.5 }} />
                              <ListItemText primary={label} />
                            </MenuItem>
                          );
                        }
                        return <MenuItem key={val} value={val}>{label}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label={f.label}
                    type={f.type || 'text'}
                    value={filterValues[f.key] || ''}
                    onChange={(e) => handleFilterChange(f.key, e.target.value)}
                    InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Collapse>
      )}
    </Box>
  );
}
